import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Video,
  BookOpen,
  Users,
  CreditCard,
  ChevronDown,
  ChevronUp,
  Layers,
  Car,
  GraduationCap,
  Award,
  BarChart3,
  ArrowDown,
  Clock,
  Target,
  TrendingUp,
  Eye,
  EyeOff,
  Handshake,
} from "lucide-react";
import ContentInsightsPanel from "../components/admin/ContentInsightsPanel";
import CommunityModeratorsPanel from "../components/admin/CommunityModeratorsPanel";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPanel() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [sections, setSections] = useState([]);
  const [modules, setModules] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [expandedSection, setExpandedSection] = useState(null);
  const [expandedModule, setExpandedModule] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [funnelFilters, setFunnelFilters] = useState({
    date_from: "",
    date_to: "",
    instructor_id: "",
    exclude_test: true,
  });
  const [showAttempts, setShowAttempts] = useState(false);
  const [insights, setInsights] = useState(null);
  const [transferSummary, setTransferSummary] = useState(null);
  const [tierSummary, setTierSummary] = useState(null);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/transfer-network/admin/summary`, { withCredentials: true })
      .then((r) => setTransferSummary(r.data))
      .catch(() => setTransferSummary(null));
    axios
      .get(`${API_URL}/api/account/admin/tier-waitlist/summary`, { withCredentials: true })
      .then((r) => setTierSummary(r.data))
      .catch(() => setTierSummary(null));
  }, []);

  // Section Dialog
  const [sectionDialog, setSectionDialog] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [sectionForm, setSectionForm] = useState({
    title: "",
    description: "",
    slug: "",
    order: 1,
    target_roles: ["learner"],
    icon: "BookOpen",
    thumbnail_url: "",
    status: "draft",
  });

  // Module Dialog
  const [moduleDialog, setModuleDialog] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [moduleForm, setModuleForm] = useState({
    section_id: "",
    title: "",
    description: "",
    order: 1,
    thumbnail_url: "",
    status: "draft",
  });

  // Lesson Dialog
  const [lessonDialog, setLessonDialog] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [lessonForm, setLessonForm] = useState({
    module_id: "",
    title: "",
    description: "",
    video_url: "",
    order: 1,
    duration_minutes: 10,
    written_content: "",
    visual_content: [],
    audio_url: "",
    audio_transcript: "",
    audio_transcript_source_url: "",
    audio_transcript_reviewed: false,
    key_points: [],
    calm_mode_content: "",
    status: "draft",
    linked_skills: [],
  });

  // Delete Confirmation
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [statsRes, sectionsRes, modulesRes, lessonsRes, funnelRes, instrRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/sections`, { withCredentials: true }),
        axios.get(`${API_URL}/api/modules`, { withCredentials: true }),
        axios.get(`${API_URL}/api/lessons`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/assessment-funnel`, { withCredentials: true, params: { exclude_test: "true" } }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/admin/instructors-list`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setSections(sectionsRes.data);
      setModules(modulesRes.data);
      setLessons(lessonsRes.data);
      if (funnelRes.data) setFunnel(funnelRes.data);
      if (instrRes.data) setInstructors(instrRes.data);
      // Fetch insights separately
      try {
        const insightsRes = await axios.get(`${API_URL}/api/admin/insights`, { withCredentials: true });
        setInsights(insightsRes.data);
      } catch {}
    } catch (error) {
      console.error("Error fetching admin data:", error);
      setLoadError(error.response?.status === 403 ? "This session does not have access to the catalogue." : "The catalogue could not be loaded.");
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const getSectionModules = (sectionId) => {
    return modules.filter((m) => m.section_id === sectionId).sort((a, b) => a.order - b.order);
  };

  const refetchFunnel = async (filters) => {
    try {
      const params = {};
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.instructor_id) params.instructor_id = filters.instructor_id;
      if (filters.exclude_test) params.exclude_test = "true";
      const { data } = await axios.get(`${API_URL}/api/admin/assessment-funnel`, { withCredentials: true, params });
      setFunnel(data);
    } catch {
      toast.error("Failed to refresh funnel data");
    }
  };

  const fetchAttempts = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/assessment-attempts`, { withCredentials: true });
      setAttempts(data.attempts || []);
      setShowAttempts(true);
    } catch {
      toast.error("Failed to load attempts");
    }
  };

  const applyFilters = (newFilters) => {
    setFunnelFilters(newFilters);
    refetchFunnel(newFilters);
  };

  const getModuleLessons = (moduleId) => {
    return lessons.filter((l) => l.module_id === moduleId).sort((a, b) => a.order - b.order);
  };

  // Jump from Content Insight panel → open lesson dialog + expand its section/module
  const openLessonById = async (lessonId) => {
    // Prefer local state; fall back to API when admin_view_role filters out drafts
    let lesson = lessons.find((l) => l.id === lessonId);
    if (!lesson) {
      try {
        const { data } = await axios.get(`${API_URL}/api/lessons/${lessonId}`, {
          withCredentials: true,
        });
        lesson = data;
      } catch {
        toast.error("Lesson not found — it may have been deleted.");
        return;
      }
    }
    const module = modules.find((m) => m.id === lesson.module_id);
    if (module) {
      setExpandedSection(module.section_id);
      setExpandedModule(module.id);
    }
    openLessonDialog(lesson.module_id, lesson);
  };

  // One-click "Create lesson for this skill" → picks best-fit module + pre-tags
  const pickBestModuleForSkill = (skill) => {
    // 1) Prefer a module whose existing lessons already mention the skill
    //    (title match or linked_skills overlap) — keeps new content grouped.
    const needle = String(skill || "").toLowerCase();
    const relevant = lessons.find((l) => {
      const title = String(l.title || "").toLowerCase();
      const linked = Array.isArray(l.linked_skills)
        ? l.linked_skills.map((s) => String(s).toLowerCase())
        : [];
      return title.includes(needle) || linked.includes(needle);
    });
    if (relevant) return relevant.module_id;

    // 2) Otherwise pick the first module in a learner-targeted section,
    //    preferring sections with target_roles including 'learner'.
    const learnerSections = sections.filter((s) =>
      Array.isArray(s.target_roles) && s.target_roles.includes("learner"),
    );
    for (const sec of learnerSections) {
      const mod = modules.find((m) => m.section_id === sec.id);
      if (mod) return mod.id;
    }

    // 3) Final fallback: first module overall
    return modules[0]?.id || null;
  };

  const openNewLessonForSkill = (skill) => {
    const clean = String(skill || "").trim();
    if (!clean) return;
    const moduleId = pickBestModuleForSkill(clean);
    if (!moduleId) {
      toast.error("Create a section + module first, then try again.");
      return;
    }
    const module = modules.find((m) => m.id === moduleId);
    if (module) {
      setExpandedSection(module.section_id);
      setExpandedModule(module.id);
    }
    const moduleLessons = getModuleLessons(moduleId);
    // Draft title — instructor edits before saving
    const title = `Mastering ${clean}`;
    setEditingLesson(null);
    setLessonForm({
      module_id: moduleId,
      title,
      description: `A focused lesson on ${clean}. Edit this draft before publishing.`,
      video_url: "",
      order: moduleLessons.length + 1,
      duration_minutes: 10,
      written_content: "",
      visual_content: [],
      audio_url: "",
    audio_transcript: "",
    audio_transcript_source_url: "",
    audio_transcript_reviewed: false,
      key_points: [],
      calm_mode_content: "",
      status: "draft",
      linked_skills: [clean],
    });
    setLessonDialog(true);
    toast.success(`Draft lesson seeded for “${clean}” — review and publish.`);
  };

  // Section CRUD
  const openSectionDialog = (section = null) => {
    if (section) {
      setEditingSection(section);
      setSectionForm({
        title: section.title,
        description: section.description,
        slug: section.slug,
        order: section.order,
        target_roles: section.target_roles || ["learner"],
        icon: section.icon || "BookOpen",
        thumbnail_url: section.thumbnail_url || "",
        status: section.status || "draft",
      });
    } else {
      setEditingSection(null);
      setSectionForm({
        title: "",
        description: "",
        slug: "",
        order: sections.length + 1,
        target_roles: ["learner"],
        icon: "BookOpen",
        thumbnail_url: "",
        status: "draft",
      });
    }
    setSectionDialog(true);
  };

  const saveSection = async () => {
    try {
      if (editingSection) {
        await axios.put(
          `${API_URL}/api/admin/sections/${editingSection.id}`,
          sectionForm,
          { withCredentials: true }
        );
        toast.success("Section updated");
      } else {
        await axios.post(`${API_URL}/api/admin/sections`, sectionForm, {
          withCredentials: true,
        });
        toast.success("Section created");
      }
      setSectionDialog(false);
      fetchData();
    } catch (error) {
      console.error("Error saving section:", error);
      toast.error(error.response?.data?.detail || "Failed to save section");
    }
  };

  // Module CRUD
  const openModuleDialog = (sectionId, module = null) => {
    if (module) {
      setEditingModule(module);
      setModuleForm({
        section_id: module.section_id,
        title: module.title,
        description: module.description,
        order: module.order,
        thumbnail_url: module.thumbnail_url || "",
        status: module.status || "draft",
      });
    } else {
      setEditingModule(null);
      const sectionModules = getSectionModules(sectionId);
      setModuleForm({
        section_id: sectionId,
        title: "",
        description: "",
        order: sectionModules.length + 1,
        thumbnail_url: "",
        status: "draft",
      });
    }
    setModuleDialog(true);
  };

  const saveModule = async () => {
    try {
      if (editingModule) {
        await axios.put(
          `${API_URL}/api/admin/modules/${editingModule.id}`,
          moduleForm,
          { withCredentials: true }
        );
        toast.success("Module updated");
      } else {
        await axios.post(`${API_URL}/api/admin/modules`, moduleForm, {
          withCredentials: true,
        });
        toast.success("Module created");
      }
      setModuleDialog(false);
      fetchData();
    } catch (error) {
      console.error("Error saving module:", error);
      toast.error("Failed to save module");
    }
  };

  // Lesson CRUD
  const openLessonDialog = (moduleId, lesson = null) => {
    if (lesson) {
      setEditingLesson(lesson);
      setLessonForm({
        module_id: lesson.module_id,
        title: lesson.title,
        description: lesson.description,
        video_url: lesson.video_url || "",
        order: lesson.order,
        duration_minutes: lesson.duration_minutes || 10,
        written_content: lesson.written_content || "",
        visual_content: lesson.visual_content || [],
        audio_url: lesson.audio_url || "",
        audio_transcript: lesson.audio_transcript || "",
        audio_transcript_source_url: lesson.audio_transcript_source_url || "",
        audio_transcript_reviewed: lesson.audio_transcript_reviewed === true,
        key_points: lesson.key_points || [],
        calm_mode_content: lesson.calm_mode_content || "",
        status: lesson.status || "draft",
        linked_skills: lesson.linked_skills || [],
      });
    } else {
      setEditingLesson(null);
      const moduleLessons = getModuleLessons(moduleId);
      setLessonForm({
        module_id: moduleId,
        title: "",
        description: "",
        video_url: "",
        order: moduleLessons.length + 1,
        duration_minutes: 10,
        written_content: "",
        visual_content: [],
        audio_url: "",
    audio_transcript: "",
    audio_transcript_source_url: "",
    audio_transcript_reviewed: false,
        key_points: [],
        calm_mode_content: "",
        status: "draft",
        linked_skills: [],
      });
    }
    setLessonDialog(true);
  };

  const transcriptGeneration = useRef(0);
  const [transcribing, setTranscribing] = useState(false);
  useEffect(() => {
    transcriptGeneration.current += 1;
    setTranscribing(false);
  }, [lessonDialog, editingLesson?.id, lessonForm.audio_url]);
  const draftTranscript = async (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error("Audio must be at most 20 MB"); return; }
    const generation = ++transcriptGeneration.current;
    const source = lessonForm.audio_url.trim();
    const form = new FormData();
    form.append("audio", file);
    setTranscribing(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/audio-transcript-draft`, form, { withCredentials: true });
      if (generation !== transcriptGeneration.current) return;
      setLessonForm(current => ({ ...current, audio_transcript: data.transcript,
        audio_transcript_source_url: source, audio_transcript_reviewed: false }));
      toast.success("Draft ready. Check the words against the recording before marking reviewed.");
    } catch (error) {
      if (generation === transcriptGeneration.current) toast.error("Transcription failed. Retry or paste the transcript.");
    } finally {
      if (generation === transcriptGeneration.current) setTranscribing(false);
    }
  };

  const saveLesson = async () => {
    try {
      if (editingLesson) {
        const { data } = await axios.put(
          `${API_URL}/api/admin/lessons/${editingLesson.id}`,
          lessonForm,
          { withCredentials: true }
        );
        if (data?.loop_closed?.title) {
          const skill = (data.loop_closed.demand_skills || [])[0];
          toast.success(
            `Loop closed — "${data.loop_closed.title}" is now live.`,
            {
              description: skill
                ? `Learners needing ${skill} can now receive recommendations.`
                : "Learners needing this skill can now receive recommendations.",
              duration: 6000,
            },
          );
        } else {
          toast.success("Lesson updated");
        }
      } else {
        await axios.post(`${API_URL}/api/admin/lessons`, lessonForm, {
          withCredentials: true,
        });
        toast.success("Lesson created");
      }
      setLessonDialog(false);
      fetchData();
    } catch (error) {
      console.error("Error saving lesson:", error);
      toast.error("Failed to save lesson");
    }
  };

  // Toggle publish/draft status inline (section / module / lesson).
  // Missing status is treated as "published" (legacy rows).
  const toggleStatus = async (type, item) => {
    const current = item.status || "published";
    const next = current === "published" ? "draft" : "published";
    const url = `${API_URL}/api/admin/${type}s/${item.id}`;
    try {
      const { data } = await axios.put(url, { status: next }, { withCredentials: true });
      if (type === "lesson" && data?.loop_closed?.title) {
        const skill = (data.loop_closed.demand_skills || [])[0];
        toast.success(
          `Loop closed — "${data.loop_closed.title}" is now live.`,
          {
            description: skill
              ? `Learners needing ${skill} can now receive recommendations.`
              : "Learners needing this skill can now receive recommendations.",
            duration: 6000,
          },
        );
      } else {
        toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} ${next === "published" ? "published" : "moved to draft"}`);
      }
      fetchData();
    } catch {
      toast.error("Could not update status");
    }
  };

  // Delete
  const confirmDelete = (type, item) => {
    setDeleteTarget({ type, item });
    setDeleteDialog(true);
  };

  const executeDelete = async () => {
    try {
      if (deleteTarget.type === "section") {
        await axios.delete(`${API_URL}/api/admin/sections/${deleteTarget.item.id}`, {
          withCredentials: true,
        });
        toast.success("Section deleted");
      } else if (deleteTarget.type === "module") {
        await axios.delete(`${API_URL}/api/admin/modules/${deleteTarget.item.id}`, {
          withCredentials: true,
        });
        toast.success("Module deleted");
      } else {
        await axios.delete(`${API_URL}/api/admin/lessons/${deleteTarget.item.id}`, {
          withCredentials: true,
        });
        toast.success("Lesson deleted");
      }
      setDeleteDialog(false);
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      console.error("Error deleting:", error);
      toast.error("Failed to delete");
    }
  };

  if (loading) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
      </div>
    );
  }

  if (loadError) return (
    <div role="alert" className="p-6 space-y-4">
      <h1>Catalogue unavailable</h1><p>{loadError} No counts are available.</p>
      <Button onClick={fetchData}>Retry</Button>
      <Link to="/admin">Back to Admin</Link>
    </div>
  );

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 pb-6">
      {/* Launch-setup resume reminder — only when admin skipped the wizard */}
      {user?.launch_setup_skipped_at && (
        <Link
          to="/launch-setup"
          data-testid="launch-setup-resume-pill"
          className="block bg-amber-50 border-b border-amber-200 hover:bg-amber-100 transition-colors"
        >
          <div className="max-w-6xl mx-auto px-4 py-2 flex items-center gap-2 text-xs sm:text-sm">
            <span className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            <span className="font-semibold text-amber-900">Launch setup paused</span>
            <span className="text-amber-700 hidden sm:inline">— Stripe & Resend probes still pending. Tap to resume.</span>
            <span className="text-amber-700 sm:hidden">— Tap to resume.</span>
            <span className="ml-auto font-semibold text-amber-700">Resume →</span>
          </div>
        </Link>
      )}
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between gap-3 min-w-0">
          <div className="flex items-center gap-4 flex-shrink-0">
            <Link to="/dashboard" className="text-slate-600 hover:text-slate-900">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <h1 className="font-bold text-slate-900">Admin Panel</h1>
          </div>
          <div className="scroll-row flex items-center gap-2 flex-1 min-w-0 justify-end">
            <Link
              to="/admin/badge-queue"
              data-testid="admin-nav-badge-queue"
              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-emerald-600 text-white rounded-lg px-3 py-2 hover:bg-emerald-700"
            >
              Badge Queue
            </Link>
            <Link
              to="/admin/learner-stories"
              data-testid="admin-nav-learner-stories"
              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-teal-600 text-white rounded-lg px-3 py-2 hover:bg-teal-700"
            >
              Learner Stories
            </Link>
            <Link
              to="/admin/content-library"
              data-testid="admin-nav-content-library"
              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-violet-600 text-white rounded-lg px-3 py-2 hover:bg-violet-700"
            >
              Content Library
            </Link>
            <Link
              to="/admin/funnel"
              data-testid="admin-nav-funnel"
              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-indigo-600 text-white rounded-lg px-3 py-2 hover:bg-indigo-700"
            >
              Funnel
            </Link>
            <Link
              to="/admin/stripe-connect"
              data-testid="admin-nav-stripe-connect"
              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-slate-900 text-white rounded-lg px-3 py-2 hover:bg-slate-800"
            >
              Stripe Connect
            </Link>
            <Link
              to="/admin/founder-mode"
              data-testid="admin-nav-founder-mode"
              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-amber-600 text-white rounded-lg px-3 py-2 hover:bg-amber-700"
            >
              Founder Onboarding
            </Link>
            <Link
              to="/admin/pricing"
              data-testid="admin-nav-pricing"
              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-emerald-600 text-white rounded-lg px-3 py-2 hover:bg-emerald-700"
            >
              Upload Pricing
            </Link>
            <Link
              to="/admin/teaching-excellence"
              data-testid="admin-nav-teaching-excellence"
              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-amber-500 text-white rounded-lg px-3 py-2 hover:bg-amber-600"
            >
              Teaching Excellence
            </Link>
            <Link
              to="/admin/curriculum"
              data-testid="admin-nav-curriculum"
              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-indigo-600 text-white rounded-lg px-3 py-2 hover:bg-indigo-700"
            >
              Curriculum Import
            </Link>
            {/* Phase H1 — admin-only hidden features activation (Feb 6, 2026) */}
            <Link
              to="/admin/funnels"
              data-testid="admin-nav-funnels-analytics"
              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-indigo-700 text-white rounded-lg px-3 py-2 hover:bg-indigo-800"
            >
              Funnels Analytics
            </Link>
            <Link
              to="/admin/beta"
              data-testid="admin-nav-beta"
              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-rose-600 text-white rounded-lg px-3 py-2 hover:bg-rose-700"
            >
              Beta Admin
            </Link>
            <Link
              to="/migration"
              data-testid="admin-nav-migration-tools"
              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-slate-700 text-white rounded-lg px-3 py-2 hover:bg-slate-800"
            >
              Migration Tools
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
          <div data-testid="stat-users" className="bg-white rounded-2xl border border-slate-200 p-4">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center mb-3">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-slate-900">{stats?.total_users || 0}</p>
            <p className="text-sm text-slate-500">Total Users</p>
          </div>
          <div data-testid="stat-learners" className="bg-white rounded-2xl border border-slate-200 p-4">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center mb-3">
              <Car className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-slate-900">{stats?.learners || 0}</p>
            <p className="text-sm text-slate-500">Learners</p>
          </div>
          <div data-testid="stat-pdis" className="bg-white rounded-2xl border border-slate-200 p-4">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center mb-3">
              <GraduationCap className="w-5 h-5 text-purple-600" />
            </div>
            <p className="text-2xl font-bold text-slate-900">{stats?.pdis || 0}</p>
            <p className="text-sm text-slate-500">PDIs</p>
          </div>
          <div data-testid="stat-adis" className="bg-white rounded-2xl border border-slate-200 p-4">
            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center mb-3">
              <Award className="w-5 h-5 text-emerald-600" />
            </div>
            <p className="text-2xl font-bold text-slate-900">{stats?.adis || 0}</p>
            <p className="text-sm text-slate-500">ADIs</p>
          </div>
          <div data-testid="stat-subscriptions" className="bg-white rounded-2xl border border-slate-200 p-4">
            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center mb-3">
              <CreditCard className="w-5 h-5 text-emerald-600" />
            </div>
            <p className="text-2xl font-bold text-slate-900">{stats?.active_subscriptions || 0}</p>
            <p className="text-sm text-slate-500">Subscribed</p>
          </div>
          <div data-testid="stat-sections" className="bg-white rounded-2xl border border-slate-200 p-4">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center mb-3">
              <Layers className="w-5 h-5 text-purple-600" />
            </div>
            <p className="text-2xl font-bold text-slate-900">{stats?.total_sections || 0}</p>
            <p className="text-sm text-slate-500">Sections</p>
          </div>
          <div data-testid="stat-modules" className="bg-white rounded-2xl border border-slate-200 p-4">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center mb-3">
              <BookOpen className="w-5 h-5 text-amber-600" />
            </div>
            <p className="text-2xl font-bold text-slate-900">{stats?.total_modules || 0}</p>
            <p className="text-sm text-slate-500">Modules</p>
          </div>
          <div data-testid="stat-lessons" className="bg-white rounded-2xl border border-slate-200 p-4">
            <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center mb-3">
              <Video className="w-5 h-5 text-red-600" />
            </div>
            <p className="text-2xl font-bold text-slate-900">{stats?.total_lessons || 0}</p>
            <p className="text-sm text-slate-500">Lessons</p>
          </div>
        </div>

        {/* Growth Ladder summary */}
        {tierSummary && (
          <div
            data-testid="growth-ladder-summary"
            className="bg-white rounded-2xl border border-slate-200 p-4"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                <Handshake className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="flex-1">
                <h2 className="font-bold text-slate-900">Growth Ladder</h2>
                <p className="text-xs text-slate-500">
                  Current tier distribution + coming-soon waitlists
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="text-center p-3 bg-violet-50 rounded-xl">
                <p className="text-xl font-bold text-violet-700" data-testid="tier-dist-trainee_pdi">
                  {tierSummary.current_tier_distribution?.trainee_pdi || 0}
                </p>
                <p className="text-[11px] text-violet-600">Trainee PDIs</p>
              </div>
              <div className="text-center p-3 bg-indigo-50 rounded-xl">
                <p className="text-xl font-bold text-indigo-700" data-testid="tier-dist-solo_adi">
                  {tierSummary.current_tier_distribution?.solo_adi || 0}
                </p>
                <p className="text-[11px] text-indigo-600">Solo ADIs</p>
              </div>
              <div className="text-center p-3 bg-emerald-50 rounded-xl">
                <p className="text-xl font-bold text-emerald-700" data-testid="tier-waitlist-coach_educator">
                  {tierSummary.waitlist_counts?.coach_educator || 0}
                </p>
                <p className="text-[11px] text-emerald-600">Coach waitlist</p>
              </div>
              <div className="text-center p-3 bg-rose-50 rounded-xl">
                <p className="text-xl font-bold text-rose-700" data-testid="tier-waitlist-driving_school">
                  {tierSummary.waitlist_counts?.driving_school || 0}
                </p>
                <p className="text-[11px] text-rose-600">School waitlist</p>
              </div>
            </div>
          </div>
        )}

        {/* Transfer Network summary */}
        {transferSummary && (
          <div
            data-testid="transfer-network-summary"
            className="bg-white rounded-2xl border border-slate-200 p-4"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
                <Handshake className="w-5 h-5 text-violet-600" />
              </div>
              <div className="flex-1">
                <h2 className="font-bold text-slate-900">Transfer Network</h2>
                <p className="text-xs text-slate-500">
                  ADI opt-ins + active transfer requests
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <div className="text-center p-3 bg-emerald-50 rounded-xl">
                <p className="text-xl font-bold text-emerald-700" data-testid="tn-adis-joined">
                  {transferSummary.adis_joined || 0}
                </p>
                <p className="text-[11px] text-emerald-600">ADIs active</p>
              </div>
              <div className="text-center p-3 bg-amber-50 rounded-xl">
                <p className="text-xl font-bold text-amber-700" data-testid="tn-adis-paused">
                  {transferSummary.adis_paused || 0}
                </p>
                <p className="text-[11px] text-amber-600">Paused</p>
              </div>
              <div className="text-center p-3 bg-indigo-50 rounded-xl">
                <p className="text-xl font-bold text-indigo-700" data-testid="tn-open-requests">
                  {transferSummary.open_requests || 0}
                </p>
                <p className="text-[11px] text-indigo-600">Open</p>
              </div>
              <div className="text-center p-3 bg-violet-50 rounded-xl">
                <p className="text-xl font-bold text-violet-700" data-testid="tn-matched-requests">
                  {transferSummary.matched_requests || 0}
                </p>
                <p className="text-[11px] text-violet-600">Matched</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-xl">
                <p className="text-xl font-bold text-slate-700" data-testid="tn-completed-requests">
                  {transferSummary.completed_requests || 0}
                </p>
                <p className="text-[11px] text-slate-500">Completed</p>
              </div>
            </div>
          </div>
        )}

        {/* Engagement Insights */}
        {insights && (
          <div data-testid="admin-insights" className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-center gap-3">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              <h2 className="font-bold text-slate-900">Engagement Insights</h2>
              <span className="text-xs text-slate-400 ml-auto">Today</span>
            </div>
            <div className="p-4">
              {/* Rate Cards with ring indicators */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
                {[
                  { label: "Check-In", pct: insights.checkin_rate, sub: `${insights.checkin_today}/${insights.total_learners}`, color: "#1e293b" },
                  { label: "Reflection", pct: insights.reflection_rate, sub: null, color: "#7c3aed" },
                  { label: "Preparation", pct: insights.prep_rate, sub: null, color: "#0ea5e9" },
                  { label: "Red Button", pct: insights.red_button_rate, sub: `${insights.red_button_today} active`, color: insights.red_button_rate > 0 ? "#ef4444" : "#94a3b8" },
                  { label: "Support Set", pct: insights.total_learners > 0 ? Math.round((insights.support_total / insights.total_learners) * 100) : 0, sub: `${insights.support_total} today`, color: "#10b981" },
                ].map((card) => (
                  <div key={card.label} className="bg-slate-50 rounded-xl p-3 flex flex-col items-center">
                    <div className="relative w-14 h-14 mb-1.5">
                      <svg className="-rotate-90" viewBox="0 0 56 56" width="56" height="56">
                        <circle cx="28" cy="28" r="24" fill="none" stroke="#f1f5f9" strokeWidth="4" />
                        <circle cx="28" cy="28" r="24" fill="none" stroke={card.color} strokeWidth="4" strokeLinecap="round" strokeDasharray={`${(card.pct / 100) * 150.8} 150.8`} />
                      </svg>
                      <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-slate-900">{card.pct}%</span>
                    </div>
                    <p className="text-[10px] text-slate-500 text-center">{card.label}</p>
                    {card.sub && <p className="text-[9px] text-slate-400">{card.sub}</p>}
                  </div>
                ))}
              </div>

              {/* Key summary stats */}
              {insights.support_total > 0 && (
                <p className="text-xs text-slate-500">{insights.support_total} learners set their support level today</p>
              )}

              {/* Weekly Trends — Line Chart */}
              {insights.trends_over_time && insights.trends_over_time.length > 1 && (() => {
                const weeks = insights.trends_over_time;
                const points = weeks.map((w) => Object.values(w.mindsets || {}).reduce((a, b) => a + b, 0));
                const maxVal = Math.max(...points, 1);
                const chartW = 400;
                const chartH = 80;
                const xStep = chartW / (points.length - 1);
                const svgPoints = points.map((v, i) => `${i * xStep},${chartH - (v / maxVal) * (chartH - 10)}`).join(" ");
                return (
                  <div className="mt-5 pt-5 border-t border-slate-100">
                    <p className="text-xs text-slate-500 mb-3">Reflections Per Week</p>
                    <div className="w-full overflow-hidden">
                      <svg viewBox={`-10 0 ${chartW + 20} ${chartH + 20}`} className="w-full h-24">
                        <polyline fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" points={svgPoints} />
                        {points.map((v, i) => (
                          <circle key={i} cx={i * xStep} cy={chartH - (v / maxVal) * (chartH - 10)} r="3" fill="#7c3aed" />
                        ))}
                        {weeks.map((w, i) => (
                          <text key={i} x={i * xStep} y={chartH + 15} textAnchor="middle" className="text-[8px]" fill="#94a3b8">{w.week.split("-W")[1] ? `W${w.week.split("-W")[1]}` : w.week}</text>
                        ))}
                        {points.map((v, i) => (
                          <text key={`v${i}`} x={i * xStep} y={chartH - (v / maxVal) * (chartH - 10) - 8} textAnchor="middle" className="text-[8px]" fill="#7c3aed" fontWeight="bold">{v}</text>
                        ))}
                      </svg>
                    </div>
                  </div>
                );
              })()}

              {/* Skill Snapshot */}
              {insights.avg_skills && Object.values(insights.avg_skills).some(v => v > 0) && (
                <div className="mt-5 pt-5 border-t border-slate-100">
                  <p className="text-xs text-slate-500 mb-2">Skills Snapshot</p>
                  <div className="flex flex-wrap gap-x-6 gap-y-1">
                    {insights.most_difficult?.length > 0 && (
                      <p className="text-xs"><span className="text-slate-400">Needs work: </span><span className="text-red-500 font-medium">{insights.most_difficult.map(([s]) => s).join(", ")}</span></p>
                    )}
                    {insights.strongest?.length > 0 && (
                      <p className="text-xs"><span className="text-slate-400">Strongest: </span><span className="text-emerald-600 font-medium">{insights.strongest.map(([s]) => s).join(", ")}</span></p>
                    )}
                  </div>
                </div>
              )}

              {/* Preparation Impact — simplified */}
              {insights.prep_correlation && insights.prep_correlation.prepped_positive_rate > insights.prep_correlation.unprepped_positive_rate && (
                <div className="mt-5 pt-5 border-t border-slate-100">
                  <p className="text-xs text-slate-500 mb-2">Preparation Impact</p>
                  <p className="text-sm text-emerald-700">
                    Learners who prepare show a <span className="font-bold">{insights.prep_correlation.prepped_positive_rate - insights.prep_correlation.unprepped_positive_rate}%</span> higher positive outcome rate.
                  </p>
                  <p className="text-[10px] text-slate-400 mt-1">{insights.total_prep_completions} total preparations completed</p>
                </div>
              )}

              {/* Rewards — simplified */}
              {insights.reward_analytics && insights.reward_analytics.total_given > 0 && (
                <div className="mt-5 pt-5 border-t border-slate-100">
                  <p className="text-xs text-slate-500 mb-1">Rewards</p>
                  <p className="text-sm text-slate-700">{insights.reward_analytics.total_given} rewards given to {insights.reward_analytics.learners_rewarded} learners</p>
                </div>
              )}

              {/* Engagement Funnel */}
              {insights.engagement_funnel?.length > 0 && (
                <div className="mt-5 pt-5 border-t border-slate-100">
                  <p className="text-xs text-slate-500 mb-4">Engagement Funnel</p>
                  <div className="space-y-2">
                    {insights.engagement_funnel.map((step, i) => (
                      <div key={step.label}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-slate-600">{step.label}</span>
                          <span className="text-xs text-slate-500">{step.count} ({step.pct}%)</span>
                        </div>
                        <div className="w-full h-6 bg-slate-100 rounded-lg overflow-hidden relative">
                          <div
                            className={`h-full rounded-lg transition-all ${i === 0 ? "bg-slate-900" : i === insights.engagement_funnel.length - 1 ? "bg-emerald-500" : "bg-slate-700"}`}
                            style={{ width: `${Math.max(step.pct, 2)}%` }}
                          />
                        </div>
                        {i < insights.engagement_funnel.length - 1 && insights.engagement_funnel[i + 1] && step.count > 0 && (
                          <p className="text-[9px] text-slate-400 text-right mt-0.5">
                            {step.count - insights.engagement_funnel[i + 1].count} dropped ({step.count > 0 ? Math.round(((step.count - insights.engagement_funnel[i + 1].count) / step.count) * 100) : 0}%)
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Content Insight — founder dashboard */}
        <ContentInsightsPanel
          onEditLesson={openLessonById}
          onCreateLessonForSkill={openNewLessonForSkill}
        />

        {/* Community Moderators — grant/revoke ADI + PDI mods */}
        <CommunityModeratorsPanel />

        {/* Assessment Funnel */}
        {funnel && (
          <div data-testid="assessment-funnel" className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <BarChart3 className="w-5 h-5 text-blue-600" />
                <h2 className="font-bold text-slate-900">Assessment Funnel</h2>
              </div>
              <Button
                data-testid="view-attempts-btn"
                variant="outline"
                size="sm"
                onClick={fetchAttempts}
                className="text-xs"
              >
                View Recent Attempts
              </Button>
            </div>

            {/* Filters */}
            <div className="p-4 bg-slate-50 border-b border-slate-100">
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className="text-xs text-slate-500 font-medium block mb-1">From</label>
                  <input
                    data-testid="filter-date-from"
                    type="date"
                    value={funnelFilters.date_from}
                    onChange={(e) => applyFilters({ ...funnelFilters, date_from: e.target.value })}
                    className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 font-medium block mb-1">To</label>
                  <input
                    data-testid="filter-date-to"
                    type="date"
                    value={funnelFilters.date_to}
                    onChange={(e) => applyFilters({ ...funnelFilters, date_to: e.target.value })}
                    className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 font-medium block mb-1">Instructor</label>
                  <select
                    data-testid="filter-instructor"
                    value={funnelFilters.instructor_id}
                    onChange={(e) => applyFilters({ ...funnelFilters, instructor_id: e.target.value })}
                    className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white min-w-[140px]"
                  >
                    <option value="">All</option>
                    {instructors.map((i) => (
                      <option key={i.id} value={i.id}>{i.name} ({i.role.toUpperCase()})</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-2 h-9">
                  <input
                    data-testid="filter-exclude-test"
                    type="checkbox"
                    id="exclude-test"
                    checked={funnelFilters.exclude_test}
                    onChange={(e) => applyFilters({ ...funnelFilters, exclude_test: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-300 text-blue-600"
                  />
                  <label htmlFor="exclude-test" className="text-sm text-slate-700 font-medium whitespace-nowrap">
                    Exclude test users
                  </label>
                </div>
                {(funnelFilters.date_from || funnelFilters.date_to || funnelFilters.instructor_id) && (
                  <button
                    data-testid="clear-filters-btn"
                    onClick={() => applyFilters({ date_from: "", date_to: "", instructor_id: "", exclude_test: funnelFilters.exclude_test })}
                    className="text-xs text-blue-600 hover:underline h-9 flex items-center"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            </div>

            <div className="p-4 space-y-6">
              {/* Funnel Steps */}
              <div className="space-y-1">
                {[
                  { label: "Started Assessment", key: "started", color: "bg-blue-500" },
                  { label: "Completed Assessment", key: "completed", color: "bg-blue-400", rateKey: "completion_rate" },
                  { label: "Viewed Results", key: "results_viewed", color: "bg-indigo-400", rateKey: "results_viewed_rate" },
                  { label: "Clicked Upgrade", key: "upgrade_clicked", color: "bg-purple-400", rateKey: "upgrade_click_rate" },
                  { label: "Paid", key: "payment_completed", color: "bg-emerald-500", rateKey: "payment_rate" },
                ].map((step, i) => {
                  const count = funnel.funnel[step.key] || 0;
                  const started = funnel.funnel.started || 1;
                  const widthPct = Math.max((count / started) * 100, 8);
                  const rate = step.rateKey ? funnel.rates[step.rateKey] : null;
                  const prevKey = i > 0 ? ["started", "completed", "results_viewed", "upgrade_clicked", "payment_completed"][i - 1] : null;
                  const prevCount = prevKey ? (funnel.funnel[prevKey] || 0) : 0;
                  const dropoff = i > 0 && prevCount > 0 ? prevCount - count : null;

                  return (
                    <div key={step.key}>
                      {dropoff !== null && dropoff > 0 && (
                        <div className="flex items-center gap-2 pl-2 py-1">
                          <ArrowDown className="w-3 h-3 text-red-400" />
                          <span className="text-xs text-red-500 font-medium">
                            -{dropoff} dropped ({prevCount > 0 ? Math.round((dropoff / prevCount) * 100) : 0}% drop-off)
                          </span>
                        </div>
                      )}
                      <div
                        data-testid={`funnel-step-${step.key}`}
                        className="flex items-center gap-4 py-2"
                      >
                        <div className="w-36 text-sm text-slate-700 font-medium flex-shrink-0">
                          {step.label}
                        </div>
                        <div className="flex-1 bg-slate-100 rounded-full h-8 overflow-hidden">
                          <div
                            className={`${step.color} h-full rounded-full flex items-center justify-end pr-3 transition-all`}
                            style={{ width: `${widthPct}%`, minWidth: "40px" }}
                          >
                            <span className="text-xs font-bold text-white">{count}</span>
                          </div>
                        </div>
                        {rate !== null && (
                          <span className="text-xs text-slate-500 w-12 text-right flex-shrink-0">{rate}%</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Key Metrics Row */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-2 border-t border-slate-100">
                <div data-testid="metric-conversion" className="text-center p-3 bg-slate-50 rounded-xl">
                  <p className="text-2xl font-black text-blue-600">{funnel.rates.overall_conversion}%</p>
                  <p className="text-xs text-slate-500 mt-1">Assessment &rarr; Paid</p>
                </div>
                <div data-testid="metric-avg-time" className="text-center p-3 bg-slate-50 rounded-xl">
                  <p className="text-2xl font-black text-slate-900">
                    {funnel.avg_time_seconds
                      ? funnel.avg_time_seconds < 60
                        ? `${Math.round(funnel.avg_time_seconds)}s`
                        : `${Math.round(funnel.avg_time_seconds / 60)}m ${Math.round(funnel.avg_time_seconds % 60)}s`
                      : "—"}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Avg. Completion Time</p>
                </div>
                <div data-testid="metric-completion" className="text-center p-3 bg-slate-50 rounded-xl">
                  <p className="text-2xl font-black text-emerald-600">{funnel.rates.completion_rate}%</p>
                  <p className="text-xs text-slate-500 mt-1">Completion Rate</p>
                </div>
              </div>

              {/* Level Breakdown */}
              {Object.keys(funnel.level_breakdown || {}).length > 0 && (
                <div className="pt-2 border-t border-slate-100">
                  <p className="text-sm font-semibold text-slate-700 mb-3">Breakdown by Level</p>
                  <div className="grid grid-cols-3 gap-3">
                    {["Early Stage", "Developing", "Nearly Test Ready"].map((level) => {
                      const count = funnel.level_breakdown[level] || 0;
                      const total = Object.values(funnel.level_breakdown).reduce((a, b) => a + b, 0);
                      const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                      const colors = {
                        "Early Stage": "bg-amber-100 text-amber-700 border-amber-200",
                        "Developing": "bg-blue-100 text-blue-700 border-blue-200",
                        "Nearly Test Ready": "bg-emerald-100 text-emerald-700 border-emerald-200",
                      };
                      return (
                        <div
                          key={level}
                          data-testid={`level-${level.toLowerCase().replace(/\s+/g, "-")}`}
                          className={`rounded-xl border p-3 text-center ${colors[level]}`}
                        >
                          <p className="text-xl font-bold">{count}</p>
                          <p className="text-xs font-medium mt-0.5">{level}</p>
                          <p className="text-xs opacity-70">{pct}%</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Recent Assessment Attempts */}
        {showAttempts && (
          <div data-testid="assessment-attempts" className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-bold text-slate-900">Recent Assessment Attempts</h2>
              <button onClick={() => setShowAttempts(false)} className="text-xs text-slate-500 hover:text-slate-700">Hide</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left p-3 font-medium text-slate-500">User</th>
                    <th className="text-left p-3 font-medium text-slate-500">Type</th>
                    <th className="text-left p-3 font-medium text-slate-500">Level</th>
                    <th className="text-left p-3 font-medium text-slate-500">Weakness</th>
                    <th className="text-left p-3 font-medium text-slate-500">Instructor</th>
                    <th className="text-center p-3 font-medium text-slate-500">Upgrade Click</th>
                    <th className="text-center p-3 font-medium text-slate-500">Paid</th>
                    <th className="text-left p-3 font-medium text-slate-500">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {attempts.map((a) => (
                    <tr key={a.user_id} className={a.is_test_user ? "bg-yellow-50/50" : ""}>
                      <td className="p-3">
                        <p className="font-medium text-slate-900">{a.name}</p>
                        <p className="text-xs text-slate-400">{a.email}</p>
                      </td>
                      <td className="p-3">
                        {a.is_test_user ? (
                          <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-medium">Test</span>
                        ) : (
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">Real</span>
                        )}
                      </td>
                      <td className="p-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          a.level === 1 ? "bg-amber-100 text-amber-700" :
                          a.level === 2 ? "bg-blue-100 text-blue-700" :
                          "bg-emerald-100 text-emerald-700"
                        }`}>
                          {a.level_name}
                        </span>
                      </td>
                      <td className="p-3 text-slate-600 max-w-[150px] truncate">{a.weakness}</td>
                      <td className="p-3 text-slate-600">{a.instructor_name || "—"}</td>
                      <td className="p-3 text-center">
                        {a.clicked_upgrade ? (
                          <span className="text-emerald-600 font-bold">Yes</span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        {a.paid ? (
                          <span className="text-emerald-600 font-bold">Paid</span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="p-3 text-xs text-slate-500 whitespace-nowrap">
                        {a.assessment_date ? new Date(a.assessment_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                      </td>
                    </tr>
                  ))}
                  {attempts.length === 0 && (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-slate-400">No assessment attempts yet</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Content Sections */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-bold text-slate-900">Content Sections</h2>
            <Button
              data-testid="add-section-btn"
              onClick={() => openSectionDialog()}
              size="sm"
              className="bg-blue-700 hover:bg-blue-800"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Section
            </Button>
          </div>

          <div className="divide-y divide-slate-100">
            {sections.map((section, sectionIndex) => {
              const sectionModules = getSectionModules(section.id);
              const isSectionExpanded = expandedSection === section.id;

              return (
                <div key={section.id} data-testid={`admin-section-${sectionIndex}`}>
                  {/* Section Row */}
                  <div className="p-4 flex items-center gap-4 bg-slate-50">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Layers className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-900">{section.title}</p>
                        <StatusPill status={section.status} onToggle={() => toggleStatus("section", section)} />
                      </div>
                      <p className="text-xs text-slate-500">
                        {sectionModules.length} modules · {section.target_roles?.join(", ")} · /{section.slug}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        data-testid={`edit-section-${sectionIndex}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => openSectionDialog(section)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid={`delete-section-${sectionIndex}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => confirmDelete("section", section)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid={`toggle-section-${sectionIndex}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedSection(isSectionExpanded ? null : section.id)}
                      >
                        {isSectionExpanded ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Section Content (Modules) */}
                  {isSectionExpanded && (
                    <div className="border-t border-slate-100">
                      {sectionModules.map((module, moduleIndex) => {
                        const moduleLessons = getModuleLessons(module.id);
                        const isModuleExpanded = expandedModule === module.id;

                        return (
                          <div key={module.id} data-testid={`admin-module-${sectionIndex}-${moduleIndex}`}>
                            {/* Module Row */}
                            <div className="p-4 pl-8 flex items-center gap-4">
                              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                                <BookOpen className="w-5 h-5 text-amber-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-slate-900">{module.title}</p>
                                  <StatusPill status={module.status} onToggle={() => toggleStatus("module", module)} />
                                </div>
                                <p className="text-xs text-slate-500">
                                  {moduleLessons.length} lessons · Order: {module.order}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openModuleDialog(section.id, module)}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => confirmDelete("module", module)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setExpandedModule(isModuleExpanded ? null : module.id)}
                                >
                                  {isModuleExpanded ? (
                                    <ChevronUp className="w-4 h-4" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4" />
                                  )}
                                </Button>
                              </div>
                            </div>

                            {/* Lessons */}
                            {isModuleExpanded && (
                              <div className="bg-slate-50 border-t border-slate-100">
                                {moduleLessons.map((lesson, lessonIndex) => (
                                  <div
                                    key={lesson.id}
                                    className="px-4 pl-16 py-3 flex items-center gap-4 border-b border-slate-100 last:border-b-0"
                                  >
                                    <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center border border-slate-200">
                                      <Video className="w-4 h-4 text-slate-400" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <p className="font-medium text-slate-900 text-sm">{lesson.title}</p>
                                        <StatusPill status={lesson.status} onToggle={() => toggleStatus("lesson", lesson)} />
                                      </div>
                                      <p className="text-xs text-slate-500 truncate">{lesson.video_url}</p>
                                      {lesson.linked_skills?.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-1">
                                          {lesson.linked_skills.slice(0, 3).map(s => (
                                            <span key={s} className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 font-semibold">{s}</span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => openLessonDialog(module.id, lesson)}
                                      >
                                        <Pencil className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => confirmDelete("lesson", lesson)}
                                        className="text-red-600 hover:text-red-700"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                                <div className="p-3 pl-16">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openLessonDialog(module.id)}
                                    className="w-full"
                                  >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Add Lesson
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      <div className="p-3 pl-8">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openModuleDialog(section.id)}
                          className="w-full"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add Module to {section.title}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {sections.length === 0 && (
              <div className="p-8 text-center">
                <p className="text-slate-500">No sections yet. Create your first section!</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section Dialog */}
      <Dialog open={sectionDialog} onOpenChange={setSectionDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingSection ? "Edit Section" : "Add New Section"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="section-title">Title</Label>
              <Input
                id="section-title"
                value={sectionForm.title}
                onChange={(e) => setSectionForm({ ...sectionForm, title: e.target.value })}
                placeholder="e.g., Core Driving Skills"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="section-slug">URL Slug</Label>
              <Input
                id="section-slug"
                value={sectionForm.slug}
                onChange={(e) => setSectionForm({ ...sectionForm, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })}
                placeholder="e.g., core-skills"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="section-description">Description</Label>
              <Textarea
                id="section-description"
                value={sectionForm.description}
                onChange={(e) => setSectionForm({ ...sectionForm, description: e.target.value })}
                placeholder="Brief description of this section"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="section-order">Order</Label>
                <Input
                  id="section-order"
                  type="number"
                  value={sectionForm.order}
                  onChange={(e) => setSectionForm({ ...sectionForm, order: parseInt(e.target.value) || 1 })}
                  min={1}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="section-roles">Target Roles</Label>
                <Select
                  value={sectionForm.target_roles[0]}
                  onValueChange={(v) => setSectionForm({ ...sectionForm, target_roles: [v] })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="learner">Learner</SelectItem>
                    <SelectItem value="pdi">PDI</SelectItem>
                    <SelectItem value="adi">ADI</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Visibility</Label>
              <Select
                value={sectionForm.status || "draft"}
                onValueChange={(v) => setSectionForm({ ...sectionForm, status: v })}
              >
                <SelectTrigger data-testid="section-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft (hidden from learners)</SelectItem>
                  <SelectItem value="published">Published (live)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSectionDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveSection} className="bg-blue-700 hover:bg-blue-800">
              {editingSection ? "Save Changes" : "Create Section"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Module Dialog */}
      <Dialog open={moduleDialog} onOpenChange={setModuleDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingModule ? "Edit Module" : "Add New Module"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="module-title">Title</Label>
              <Input
                id="module-title"
                value={moduleForm.title}
                onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })}
                placeholder="e.g., Core Control"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="module-description">Description</Label>
              <Textarea
                id="module-description"
                value={moduleForm.description}
                onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })}
                placeholder="Brief description of this module"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="module-order">Order</Label>
                <Input
                  id="module-order"
                  type="number"
                  value={moduleForm.order}
                  onChange={(e) => setModuleForm({ ...moduleForm, order: parseInt(e.target.value) || 1 })}
                  min={1}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="module-thumbnail">Thumbnail URL (optional)</Label>
              <Input
                id="module-thumbnail"
                value={moduleForm.thumbnail_url}
                onChange={(e) => setModuleForm({ ...moduleForm, thumbnail_url: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <div className="space-y-2">
              <Label>Visibility</Label>
              <Select
                value={moduleForm.status || "draft"}
                onValueChange={(v) => setModuleForm({ ...moduleForm, status: v })}
              >
                <SelectTrigger data-testid="module-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft (hidden from learners)</SelectItem>
                  <SelectItem value="published">Published (live)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModuleDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveModule} className="bg-blue-700 hover:bg-blue-800">
              {editingModule ? "Save Changes" : "Create Module"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lesson Dialog */}
      <Dialog open={lessonDialog} onOpenChange={setLessonDialog}>
        <DialogContent className="sm:max-w-lg max-h-screen-safe overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingLesson ? "Edit Lesson" : "Add New Lesson"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="lesson-title">Title</Label>
              <Input
                id="lesson-title"
                value={lessonForm.title}
                onChange={(e) => setLessonForm({ ...lessonForm, title: e.target.value })}
                placeholder="e.g., Introduction to Steering"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lesson-description">Description</Label>
              <Textarea
                id="lesson-description"
                value={lessonForm.description}
                onChange={(e) => setLessonForm({ ...lessonForm, description: e.target.value })}
                placeholder="What this lesson covers"
                rows={2}
              />
            </div>
            
            {/* Multi-Format Content Section */}
            <div className="border-t border-slate-200 pt-4">
              <h4 className="font-semibold text-slate-900 mb-3">Content Formats</h4>
              
              {/* Written Content */}
              <div className="space-y-2 mb-4">
                <Label htmlFor="lesson-written">Written Content (Step-by-Step)</Label>
                <Textarea
                  id="lesson-written"
                  value={lessonForm.written_content}
                  onChange={(e) => setLessonForm({ ...lessonForm, written_content: e.target.value })}
                  placeholder="Step-by-step written instructions..."
                  rows={4}
                />
              </div>
              
              {/* Key Points */}
              <div className="space-y-2 mb-4">
                <Label htmlFor="lesson-keypoints">Key Points (one per line)</Label>
                <Textarea
                  id="lesson-keypoints"
                  value={lessonForm.key_points?.join("\n") || ""}
                  onChange={(e) => setLessonForm({ 
                    ...lessonForm, 
                    key_points: e.target.value.split("\n").filter(p => p.trim())
                  })}
                  placeholder="Key point 1&#10;Key point 2&#10;Key point 3"
                  rows={3}
                />
                <p className="text-xs text-slate-500">For repetition-based learners</p>
              </div>
              
              {/* Video URL */}
              <div className="space-y-2 mb-4">
                <Label htmlFor="lesson-video">YouTube Video URL</Label>
                <Input
                  id="lesson-video"
                  value={lessonForm.video_url}
                  onChange={(e) => setLessonForm({ ...lessonForm, video_url: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=..."
                />
              </div>
              
              {/* Audio URL */}
              <div className="space-y-2 mb-4">
                <Label htmlFor="lesson-audio">Audio URL (optional)</Label>
                <Input
                  id="lesson-audio"
                  value={lessonForm.audio_url}
                  onChange={(e) => setLessonForm({ ...lessonForm, audio_url: e.target.value, audio_transcript: "", audio_transcript_source_url: "", audio_transcript_reviewed: false })}
                  placeholder="https://... (MP3 or audio file URL)"
                />
              </div>
              
              <div className="space-y-2 mb-4">
                <Label htmlFor="lesson-transcript">Recording transcript for Karen</Label>
                <p className="text-xs text-slate-500">For audio-only lessons, upload the original recording or paste its words. Check the transcript before allowing Karen to read it. Written lesson content takes priority.</p>
                <Input type="file" accept="audio/*" disabled={transcribing || !lessonForm.audio_url.trim()}
                  aria-label="Original recording for transcript"
                  onChange={e => { draftTranscript(e.target.files?.[0]); e.target.value = ""; }} />
                {transcribing && <p role="status">Creating transcript draft…</p>}
                <Textarea id="lesson-transcript" rows={6} value={lessonForm.audio_transcript || ""}
                  disabled={transcribing || !lessonForm.audio_url.trim()}
                  onChange={e => setLessonForm({ ...lessonForm, audio_transcript: e.target.value,
                    audio_transcript_source_url: lessonForm.audio_url.trim(), audio_transcript_reviewed: false })} />
                <label className="flex gap-2 items-center">
                  <input type="checkbox" checked={lessonForm.audio_transcript_reviewed === true}
                    disabled={transcribing || !lessonForm.audio_transcript?.trim() || lessonForm.audio_transcript_source_url !== lessonForm.audio_url.trim()}
                    onChange={e => setLessonForm({ ...lessonForm, audio_transcript_reviewed: e.target.checked })} />
                  I checked this transcript against the selected recording
                </label>
              </div>

              {/* Calm Mode Content */}
              <div className="space-y-2 mb-4">
                <Label htmlFor="lesson-calm">Calm Mode Content (optional)</Label>
                <Textarea
                  id="lesson-calm"
                  value={lessonForm.calm_mode_content}
                  onChange={(e) => setLessonForm({ ...lessonForm, calm_mode_content: e.target.value })}
                  placeholder="Simplified, calmer version of the content..."
                  rows={3}
                />
                <p className="text-xs text-slate-500">Simplified version for nervous/neurodiverse learners</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="lesson-order">Order</Label>
                <Input
                  id="lesson-order"
                  type="number"
                  value={lessonForm.order}
                  onChange={(e) => setLessonForm({ ...lessonForm, order: parseInt(e.target.value) || 1 })}
                  min={1}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lesson-duration">Duration (min)</Label>
                <Input
                  id="lesson-duration"
                  type="number"
                  value={lessonForm.duration_minutes}
                  onChange={(e) => setLessonForm({ ...lessonForm, duration_minutes: parseInt(e.target.value) || 10 })}
                  min={1}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="lesson-linked-skills">Linked weak-area skills (comma-separated)</Label>
              <Input
                id="lesson-linked-skills"
                data-testid="lesson-linked-skills"
                value={(lessonForm.linked_skills || []).join(", ")}
                onChange={(e) => setLessonForm({
                  ...lessonForm,
                  linked_skills: e.target.value.split(",").map(s => s.trim()).filter(Boolean),
                })}
                placeholder="e.g. Roundabouts, Mirror checks, Junctions"
              />
              <p className="text-xs text-slate-500">
                Used to recommend this lesson to learners when those areas come up as weaknesses.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Visibility</Label>
              <Select
                value={lessonForm.status || "draft"}
                onValueChange={(v) => setLessonForm({ ...lessonForm, status: v })}
              >
                <SelectTrigger data-testid="lesson-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft (hidden from learners)</SelectItem>
                  <SelectItem value="published">Published (live)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLessonDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveLesson} className="bg-blue-700 hover:bg-blue-800">
              {editingLesson ? "Save Changes" : "Create Lesson"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600 py-4">
            Are you sure you want to delete this {deleteTarget?.type}?
            {deleteTarget?.type === "section" && " This will also delete all modules and lessons in this section."}
            {deleteTarget?.type === "module" && " This will also delete all lessons in this module."}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(false)}>
              Cancel
            </Button>
            <Button onClick={executeDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─── StatusPill — small helper to show draft/published state in the CMS tree.
   Missing status is treated as "published" (legacy rows created before the field existed). ─── */
function StatusPill({ status, onToggle }) {
  const isPub = !status || status === "published";
  return (
    <button
      data-testid={`cms-status-toggle`}
      onClick={(e) => { e.stopPropagation(); onToggle?.(); }}
      title={isPub ? "Click to unpublish" : "Click to publish"}
      className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition ${
        isPub
          ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
          : "bg-amber-100 text-amber-700 hover:bg-amber-200"
      }`}>
      {isPub ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
      {isPub ? "Live" : "Draft"}
    </button>
  );
}

